import type { NlsKey } from '../../nls/strings_nls'
import type { WorkspaceRole } from '../../types/workspace'
import { isModuleEnabled, type ModuleKey, type WorkspaceModuleSource } from '../../utils/workspaceModules'
import { getHelperSnapshot, getHelperEduJobs, getHelperOpsPhoneItems } from '../helper/helperStore'
import { getMemberOpsPhoneItems } from '../member/memberOpsStore'
import type { OpsPhoneItemKey } from '../../../functions-common/src/ops/phoneItems'

export type Surface = 'member' | 'helper'

export type MemberTabKey =
  | 'for-you'
  | 'board'
  | 'nexus'
  | 'ai'
  | 'stock'
  | 'forms'
  | 'sync'
  | 'automations'
  | 'team'
  | 'class'
  | 'ops'

export type HelperTabKey =
  | 'tasks'
  | 'field'
  | 'chat'
  | 'calendar'
  | 'meetings'
  | 'school'
  | 'ops'

export type TabKey = MemberTabKey | HelperTabKey

export const MEMBER_TAB_KEYS: readonly MemberTabKey[] = [
  'for-you',
  'board',
  'nexus',
  'ai',
  'stock',
  'forms',
  'sync',
  'automations',
  'team',
  'class',
  'ops',
] as const

export const HELPER_TAB_KEYS: readonly HelperTabKey[] = [
  'tasks',
  'field',
  'chat',
  'calendar',
  'meetings',
  'school',
  'ops',
] as const

export interface TabAccessContext {
  role?: WorkspaceRole
  canManageInvites?: boolean
  canManageFieldPasses?: boolean
  workspace?: WorkspaceModuleSource
  modules?: Record<string, unknown>
  eduJobs?: string[]
  opsPhoneItems?: OpsPhoneItemKey[]
  [key: string]: unknown
}

export interface TabDef<K extends TabKey = TabKey> {
  key: K
  surface: Surface
  labelKey: NlsKey
  labelKeyFor?: (ctx: TabAccessContext) => NlsKey
  icon: string
  fixedFirst?: true
  module?: ModuleKey
  roles?: WorkspaceRole[]
  route: string
  unreadSource?: string
  /**
   * Feature gate flag. When false, the engine drops this tab regardless of modules or prefs (TB-D14).
   * Defaults to true if undefined.
   */
  available?: boolean
  /**
   * Optional access predicate (e.g. for R39 sector ops, custom role gates, or dynamic permissions).
   */
  accessPredicate?: (ctx: TabAccessContext) => boolean
}

export const MEMBER_TAB_CATALOG: readonly TabDef<MemberTabKey>[] = [
  {
    key: 'for-you',
    surface: 'member',
    labelKey: 'NLSMemberTabTodos',
    icon: 'for-you',
    fixedFirst: true,
    route: '/member/:slug/for-you',
    unreadSource: 'tasks',
  },
  {
    key: 'board',
    surface: 'member',
    labelKey: 'NLSMemberTabBoard',
    icon: 'board',
    route: '/member/:slug/board',
  },
  {
    key: 'nexus',
    surface: 'member',
    labelKey: 'NLSMemberTabNexus',
    icon: 'nexus',
    route: '/member/:slug/nexus',
    unreadSource: 'nexus',
  },
  {
    key: 'ai',
    surface: 'member',
    labelKey: 'NLSMemberTabAiMagic',
    icon: 'ai',
    route: '/member/:slug/ai',
  },
  {
    key: 'stock',
    surface: 'member',
    labelKey: 'NLSMemberTabStock',
    icon: 'stock',
    module: 'stock',
    // Stock tab exists in the catalog but is unavailable until R26 FI18 lands (TB-D14)
    available: false,
    route: '/member/:slug/stock',
  },
  {
    key: 'forms',
    surface: 'member',
    labelKey: 'NLSMemberTabForms',
    icon: 'forms',
    module: 'forms',
    route: '/member/:slug/forms',
    unreadSource: 'forms',
  },
  {
    key: 'sync',
    surface: 'member',
    labelKey: 'NLSMemberTabSync',
    icon: 'sync',
    route: '/member/:slug/sync',
    unreadSource: 'sync',
  },
  {
    key: 'automations',
    surface: 'member',
    labelKey: 'NLSMemberTabAutomations',
    icon: 'automations',
    module: 'automate',
    roles: ['OWNER', 'ADMIN', 'EDITOR'],
    route: '/member/:slug/automations',
  },
  {
    key: 'team',
    surface: 'member',
    labelKey: 'NLSMemberTabTeam',
    icon: 'team',
    route: '/member/:slug/team',
  },
  {
    key: 'class',
    surface: 'member',
    labelKey: 'NLSMemberTabClass',
    icon: 'class',
    // Gated by education modules (teaching or training) per R28
    module: 'teaching',
    route: '/member/:slug/class',
  },
  {
    key: 'ops',
    surface: 'member',
    labelKey: 'NLSMemberTabOperations',
    labelKeyFor: (ctx) => resolveOpsTabLabelKey(ctx, 'member'),
    icon: 'ops',
    route: '/member/:slug/ops',
    accessPredicate: (ctx: TabAccessContext) => {
      if (Array.isArray(ctx.opsPhoneItems)) {
        return ctx.opsPhoneItems.length > 0
      }
      const ws = ctx.workspace as { id?: string } | undefined
      const wsId = ws?.id
      const items = getMemberOpsPhoneItems(wsId)
      return Boolean(items && items.length > 0)
    },
  },
] as const

export const HELPER_TAB_CATALOG: readonly TabDef<HelperTabKey>[] = [
  {
    key: 'tasks',
    surface: 'helper',
    labelKey: 'NLSHelperTabTodos',
    icon: 'tasks',
    fixedFirst: true,
    route: '/helper/tasks',
    unreadSource: 'tasks',
  },
  {
    key: 'field',
    surface: 'helper',
    labelKey: 'NLSHelperTabField',
    icon: 'field',
    available: true,
    route: '/helper/field',
  },
  {
    key: 'chat',
    surface: 'helper',
    labelKey: 'NLSHelperTabChat',
    icon: 'chat',
    route: '/helper/chat',
    unreadSource: 'chat',
  },
  {
    key: 'calendar',
    surface: 'helper',
    labelKey: 'NLSHelperTabCalendar',
    icon: 'calendar',
    route: '/helper/calendar',
    unreadSource: 'calendar',
  },
  {
    key: 'meetings',
    surface: 'helper',
    labelKey: 'NLSHelperTabMeetings',
    icon: 'meetings',
    module: 'meetings',
    route: '/helper/meetings',
    unreadSource: 'meetings',
  },
  {
    key: 'school',
    surface: 'helper',
    labelKey: 'NLSHelperTabSchool',
    icon: 'school',
    // Gated by education modules (teaching or training)
    module: 'teaching',
    route: '/helper/school',
    accessPredicate: (ctx: TabAccessContext) => {
      if (Array.isArray(ctx.eduJobs)) {
        return ctx.eduJobs.length > 0
      }
      const ws = ctx.workspace as { id?: string } | undefined
      const wsId = ws?.id ?? getHelperSnapshot().activeWorkspaceId
      const jobs = wsId ? getHelperEduJobs(wsId) : getHelperSnapshot().eduJobs
      return Boolean(jobs && jobs.length > 0)
    },
  },
  {
    key: 'ops',
    surface: 'helper',
    labelKey: 'NLSHelperTabWork',
    labelKeyFor: resolveOpsTabLabelKey,
    icon: 'ops',
    route: '/helper/ops',
    accessPredicate: (ctx: TabAccessContext) => {
      if (Array.isArray(ctx.opsPhoneItems)) {
        return ctx.opsPhoneItems.length > 0
      }
      const ws = ctx.workspace as { id?: string } | undefined
      const wsId = ws?.id ?? getHelperSnapshot().activeWorkspaceId
      const items = wsId ? getHelperOpsPhoneItems(wsId) : getHelperSnapshot().opsPhoneItems
      return Boolean(items && items.length > 0)
    },
  },
] as const

export function detectActiveOpsSectors(ctx: TabAccessContext): string[] {
  const mods = (ctx.modules ?? (ctx.workspace as { modules?: Record<string, unknown> } | undefined)?.modules) as Record<string, unknown> | undefined
  const activeSectors: string[] = []

  const isSectorOn = (flag: string, moduleKey: string) => {
    if (mods && Boolean(mods[flag])) return true
    if (ctx.workspace && isModuleEnabled(ctx.workspace, moduleKey as ModuleKey)) return true
    return false
  }

  if (isSectorOn('constructionEnabled', 'construction')) activeSectors.push('construction')
  if (isSectorOn('hospitalEnabled', 'hospital')) activeSectors.push('hospital')
  if (isSectorOn('sitesEnabled', 'sites')) activeSectors.push('sites')
  if (isSectorOn('deliveryEnabled', 'delivery')) activeSectors.push('delivery')
  if (isSectorOn('shopsEnabled', 'shops')) activeSectors.push('shops')
  if (isSectorOn('staysEnabled', 'stays')) activeSectors.push('stays')

  return activeSectors
}

export function resolveOpsTabLabelKey(ctx: TabAccessContext, surface: Surface = 'helper'): NlsKey {
  const activeSectors = detectActiveOpsSectors(ctx)

  if (activeSectors.length === 1) {
    const single = activeSectors[0]
    if (single === 'construction') return surface === 'member' ? 'NLSMemberTabProjects' : 'NLSHelperTabSite'
    if (single === 'hospital') return surface === 'member' ? 'NLSMemberTabHospital' : 'NLSHelperTabWard'
    if (single === 'sites') return 'NLSWorkspaceModuleSitesTitle'
    if (single === 'delivery') return 'NLSWorkspaceModuleDeliveryTitle'
    if (single === 'shops') return 'NLSWorkspaceModuleShopsTitle'
    if (single === 'stays') return 'NLSWorkspaceModuleStaysTitle'
  }

  return surface === 'member' ? 'NLSMemberTabOperations' : 'NLSHelperTabWork'
}

export function getTabLabelKey(tabDef: TabDef, ctx?: TabAccessContext): NlsKey {
  if (ctx && tabDef.labelKeyFor) {
    return tabDef.labelKeyFor(ctx)
  }
  return tabDef.labelKey
}

export function getCatalogForSurface(surface: Surface): readonly TabDef[] {
  return surface === 'member' ? MEMBER_TAB_CATALOG : HELPER_TAB_CATALOG
}

export function getTabDef(key: TabKey, surface?: Surface): TabDef | undefined {
  if (surface) {
    return getCatalogForSurface(surface).find((tab) => tab.key === key)
  }
  return (
    MEMBER_TAB_CATALOG.find((tab) => tab.key === key) ??
    HELPER_TAB_CATALOG.find((tab) => tab.key === key)
  )
}

export type StoreHelperTabKey = 'TASKS' | 'CALENDAR' | 'MEETINGS' | 'CHAT' | 'FIELD' | 'SCHOOL' | 'OPS'

const STORE_TO_CATALOG_HELPER: Record<StoreHelperTabKey, HelperTabKey> = {
  TASKS: 'tasks',
  CALENDAR: 'calendar',
  MEETINGS: 'meetings',
  CHAT: 'chat',
  FIELD: 'field',
  SCHOOL: 'school',
  OPS: 'ops',
}

const CATALOG_TO_STORE_HELPER: Record<HelperTabKey, StoreHelperTabKey> = {
  tasks: 'TASKS',
  calendar: 'CALENDAR',
  meetings: 'MEETINGS',
  chat: 'CHAT',
  field: 'FIELD',
  school: 'SCHOOL',
  ops: 'OPS',
}

export function helperStoreToCatalogKey(key: StoreHelperTabKey | string): HelperTabKey {
  return STORE_TO_CATALOG_HELPER[key as StoreHelperTabKey] ?? (key.toLowerCase() as HelperTabKey)
}

export function helperCatalogToStoreKey(key: HelperTabKey | string): StoreHelperTabKey {
  return CATALOG_TO_STORE_HELPER[key as HelperTabKey] ?? 'TASKS'
}
